/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { string, object,
 } from 'yup';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Container,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { yupResolver } from '@hookform/resolvers/yup';
import FlashContext from '../../../../components/contexts/FlashContext';
import api from '../../../../service/axios/api';
import routing from '../../../../service/routes/routing';

const useStyles = makeStyles(theme => ({
  form: {
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  content: {
    padding: theme.spacing(4),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(8),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(4),
    background: '#f0f0f0',
    width: '100%',
  },
}));

// eslint-disable-next-line no-unused-vars
function EditIndividualForm({ individual }) {
  const { t } = useTranslation();
  const history = useHistory();
  const { notifySuccess, notifyError } = useContext(FlashContext);

  const [loading, setLoading] = useState(false);

  const filterPhone = (e, field) => {
    const valorDigitado = e.target.value;
    const apenasPermitidos = valorDigitado.replace(/[^0-9()\- ]/g, "");
    field.onChange(apenasPermitidos);
  }
  
  const filterNumber = (e, field) => {
    const valorDigitado = e.target.value;
    const apenasPermitidos = valorDigitado.replace(/\D/g, "");
    field.onChange(apenasPermitidos);
  }

  const checkCEP = async (e) => {
    const cep = e.target.value.replace(/\D/g, "");

    const clearAddress = () => {
      setValue("street_name", "");
      setValue("district", "");
      setValue("city", "");
      setValue("state", "");
      setValue("number", "");
      setValue("complement", "");
    };

    if (cep.length !== 8) {
      clearAddress();
      return;
    };

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        clearAddress();
        return;
      }

      clearErrors("cep");

      setValue("street_name", data.logradouro);
      setValue("district", data.bairro);
      setValue("city", data.localidade);
      setValue("state", data.uf);

    } catch (error) {
      console.error(error);
      clearAddress();
    }
  };

  const IndividualSchema = object({
    Individual: object({
      name: string()
        .required(t('editContractAward.required')),
    }),
    care_of: string()
      .transform((value) => (value === "" ? null : value)) 
      .nullable()
      .notRequired(),
    email: string()
      .transform((value) => (value === "" ? null : value)) 
      .nullable()
      .notRequired()
      .email(t('editContractAward.errors.email')),
    phone: string()
      .transform((value, originalValue) => {
        if (!originalValue) return null;
        const cleanStr = originalValue.replace(/\D/g, '');
        return cleanStr === "" ? null : cleanStr;
      }) 
      .nullable()
      .notRequired()
      .length(10, t('editContractAward.errors.phone')),
    mobile: string()
      .transform((value, originalValue) => {
        if (!originalValue) return null;
        const cleanStr = originalValue.replace(/\D/g, '');
        return cleanStr === "" ? null : cleanStr;
      }) 
      .nullable()
      .notRequired()
      .length(11, t('editContractAward.errors.mobile')),
    whatsapp: string()
      .transform((value, originalValue) => {
        if (!originalValue) return null;
        const cleanStr = originalValue.replace(/\D/g, '');
        return cleanStr === "" ? null : cleanStr;
      }) 
      .nullable()
      .notRequired()
      .length(11, t('editContractAward.errors.mobile')),
    postal_code: string()
      .transform((value, originalValue) => {
        if (!originalValue) return null;
        const cleanStr = originalValue.replace(/\D/g, '');
        return cleanStr === "" ? null : cleanStr;
      }) 
      .nullable()
      .notRequired()
      .length(8, t('editContractAward.errors.postalCode')),
    number: string()
      .transform((value, originalValue) => {
          if (!originalValue) return null;
          const cleanStr = originalValue.replace(/\D/g, '');
          return cleanStr === "" ? null : cleanStr;
        }) 
      .nullable()
      .notRequired(),
    complement: string()
      .transform((value) => (value === "" ? null : value)) 
      .nullable()
      .notRequired(),
    district: string()
      .transform((value) => (value === "" ? null : value)) 
      .nullable()
      .notRequired(),
    city: string()
      .transform((value) => (value === "" ? null : value)) 
      .nullable()
      .notRequired(),
    state: string()
      .transform((value) => (value === "" ? null : value)) 
      .nullable()
      .notRequired(),
  });

  const {
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: individual,
    mode: "onBlur",
    resolver: yupResolver(IndividualSchema),
  });

  const mySubmit = async (data) => {
    setLoading(true);
    const cancel = new AbortController();
    try {
      const response = await api.contract.updateAwardIndividual(cancel, individual.id, data);
      
      notifySuccess(response.message);
      if (history.length > 2) {
        history.goBack();
      } else {
        history.push(routing.contractAwards);
      }
    } catch (error) {
      notifyError(error.message);
    } finally{
      setLoading(false);
    }
  }

  const classes = useStyles();

  return (
    <form className={classes.form} onSubmit={
      handleSubmit(mySubmit)
      }>
      <Container className={classes.content}>
  
        <div className={classes.section}>
          <Typography variant="h5" component="h3" gutterBottom>
            {t('editContractAward.data')}
          </Typography>
          <Grid container spacing={10}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label={t('editContractAward.individual.document')}
                defaultValue={individual.Individual.cpf}
                variant="outlined"
                size="small"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="Individual.name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.individual.name')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!errors.Individual?.name}
                    helperText={errors.Individual?.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="care_of"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.individual.careOf')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!errors.care_of}
                    helperText={errors.care_of?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.email')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder={t('editContractAward.placeholders.email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{ inputMode: "numeric" }}
                    onChange={(e) => filterPhone(e, field)}
                    label={t('editContractAward.phone')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder={t('editContractAward.placeholders.phone')}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="mobile"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{ inputMode: "numeric" }}
                    onChange={(e) => filterPhone(e, field)}
                    label={t('editContractAward.mobile')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder={t('editContractAward.placeholders.mobile')}
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="whatsapp"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{ inputMode: "numeric" }}
                    onChange={(e) => filterPhone(e, field)}
                    label={t('editContractAward.individual.whatsapp')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder={t('editContractAward.placeholders.mobile')}
                    error={!!errors.whatsapp}
                    helperText={errors.whatsapp?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="postal_code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{ inputMode: "numeric" }}
                    onChange={(e) => {
                      field.onChange(e);
                      checkCEP(e);
                    }}
                    label={t('editContractAward.postalCode')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder={t('editContractAward.postalCode')}
                    error={!!errors.postal_code}
                    helperText={errors.postal_code?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="street_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.street')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => filterNumber(e, field)}
                    label={t('editContractAward.number')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!errors.number}
                    helperText={errors.number?.message}
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="complement"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.complement')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!errors.complement}
                    helperText={errors.complement?.message}
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="district"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.district')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.city')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Controller 
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('editContractAward.state')}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: !!field.value }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </div>
      </Container>
      <div className={classes.footer}>
        <Button type="submit" disabled={loading} variant="contained" color="primary">
          {t('editContractAward.submit')}
        </Button>
      </div>
    </form>
      
  );
}

export default EditIndividualForm;
